import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import { defineMessages, useIntl } from "react-intl";

import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import SortOrder from "@mapmycustomers/shared/enum/SortOrder";
import Resource from "@mapmycustomers/shared/types/entity/Resource";
import { FilterModel } from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import SortModel from "@mapmycustomers/shared/types/viewModel/internalModel/SortModel";
import useDebouncedCallback from "@mapmycustomers/shared/util/hook/useDebouncedCallback";
import { LoadingSpinner, Modal, SearchField } from "@mapmycustomers/ui";

import ResourceType from "@app/enum/ResourceType";
import { fetchPinnedResources, fetchResources } from "@app/store/resources/actions";
import useAnalytics from "@app/util/contexts/useAnalytics";
import resourceFieldModel, { ResourceFieldName } from "@app/util/fieldModel/ResourceFieldModel";
import {
  createEmptyFilterModel,
  createSimpleCondition,
  upsertSimpleConditions,
} from "@app/util/filters/utils";

import PinnedResources from "./PinnedResources";
import ResourceItem from "./ResourceItem";
import styles from "./ResourcesSelectModal.module.scss";
import Toolbar from "./Toolbar";

const messages = defineMessages({
  attach: {
    id: "component.resourcesSelectModal.attach",
    defaultMessage:
      "Attach{multiselect, select, true {{count, select, 0 {} other { ({count})}}} other {}}",
    description: "Attach button label",
  },
  count: {
    id: "component.resourcesSelectModal.list.count",
    defaultMessage: "{total, select, 0 {0 shown} other {{count} of {total} shown}}",
    description: "Resources count info",
  },
  empty: {
    id: "component.resourcesSelectModal.list.data.empty",
    defaultMessage: "No resources found",
    description: "No resources found info",
  },
  search: {
    id: "component.resourcesSelectModal.search",
    defaultMessage: "Search for resource",
    description: "Search for resource placeholder",
  },
  title: {
    id: "component.resourcesSelectModal.title",
    defaultMessage: "Select {multiselect, select, true {Resources} other {Resource}}",
    description: "Select Resource modal title",
  },
});

const DEFAULT_RESOURCE_TYPES = [ResourceType.FILE, ResourceType.LINK];

export interface ResourcesSelectModalProps {
  multiselect?: boolean;
  onCancel: () => void;
  onConfirm: (selectedResources: Resource[]) => void;
  open: boolean;
  resourceTypes?: ResourceType[];
  selectedResources?: Resource[];
  title?: React.ReactNode;
}

const ResourcesSelectModal = ({
  multiselect = false,
  onCancel,
  onConfirm,
  onFetchPinnedResources,
  onFetchResources,
  open,
  resourceTypes = DEFAULT_RESOURCE_TYPES,
  selectedResources,
  title,
}: ResourcesSelectModalProps & typeof mapDispatchToProps) => {
  const intl = useIntl();
  const analytics = useAnalytics();

  const [resources, setResources] = useState<Resource[]>();
  const [pinnedResources, setPinnedResources] = useState<Resource[]>([]);

  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [updatedSelectedResources, setSelectedResources] = useState<Resource[]>(
    selectedResources ?? []
  );

  const [filter, setFilter] = useState<FilterModel>(createEmptyFilterModel());
  const [sort, setSort] = useState<SortModel>([
    { field: resourceFieldModel.getByName(ResourceFieldName.UPDATED_AT)!, order: SortOrder.DESC },
  ]);

  const handleSearch = useDebouncedCallback(
    [
      (value: string) => {
        setFilter((filter) =>
          upsertSimpleConditions(
            filter,
            createSimpleCondition(ResourceFieldName.NAME, FilterOperator.CONTAINS, value)
          )
        );
        analytics.initiated(["Filter"]);
      },
      500,
    ],
    []
  );

  const handleChangeSelect = useCallback(
    (resource: Resource) => {
      setSelectedResources((selectedResources) => {
        const isSelected = selectedResources.some(({ id }) => id === resource.id);

        if (multiselect) {
          if (isSelected) {
            return selectedResources.filter(({ id }) => id !== resource.id);
          } else {
            return [...selectedResources, resource];
          }
        } else {
          return isSelected ? [] : [resource];
        }
      });
    },
    [multiselect]
  );

  const handleConfirm = useCallback(() => {
    if (updatedSelectedResources) {
      onConfirm(Array.from(updatedSelectedResources));
    }
  }, [onConfirm, updatedSelectedResources]);

  useEffect(() => {
    setLoading(true);
    let filterPayload = filter;
    if (!resourceTypes.includes(ResourceType.LINK)) {
      filterPayload = upsertSimpleConditions(
        filterPayload,
        createSimpleCondition(ResourceFieldName.URL, FilterOperator.EMPTY)
      );
    } else if (!resourceTypes.includes(ResourceType.FILE)) {
      filterPayload = upsertSimpleConditions(
        filterPayload,
        createSimpleCondition(ResourceFieldName.URL, FilterOperator.NOT_EMPTY)
      );
    }
    onFetchResources({
      failureCallback: () => {
        setLoading(false);
      },
      successCallback: ({ resources, total }) => {
        setResources(resources);
        setTotal(total);
        setLoading(false);
      },
      viewState: { filter: filterPayload, sort },
    });
  }, [filter, onFetchResources, resourceTypes, sort]);

  useEffect(() => {
    onFetchPinnedResources({
      failureCallback: () => {
        setLoading(false);
      },
      successCallback: (resources) => {
        setPinnedResources(resources);
      },
    });
  }, [onFetchPinnedResources]);

  return (
    <Modal
      okButtonProps={{ disabled: !updatedSelectedResources.length }}
      okText={intl.formatMessage(messages.attach, {
        count: updatedSelectedResources.length,
        multiselect,
      })}
      onCancel={onCancel}
      onOk={handleConfirm}
      open={open}
      scrollable
      title={title ?? intl.formatMessage(messages.title, { multiselect })}
      width="34.25rem"
    >
      <div className={styles.container}>
        <SearchField
          allowClear
          className={styles.searchField}
          onChange={handleSearch}
          placeholder={intl.formatMessage(messages.search)}
          size="large"
        />
        <Toolbar
          filter={filter}
          onFilterChange={setFilter}
          onSortChange={setSort}
          resourceTypes={resourceTypes}
          sort={sort}
        />
        <div className={styles.count}>
          {intl.formatMessage(messages.count, { count: resources?.length ?? 0, total })}
        </div>
        <div className={styles.overflowContainer}>
          {loading ? (
            <LoadingSpinner />
          ) : (
            <>
              <PinnedResources
                multiselect={multiselect}
                onChangeSelect={handleChangeSelect}
                pinnedResources={pinnedResources}
                selectedResources={updatedSelectedResources}
              />
              <div className={styles.list}>
                {resources?.length ? (
                  resources.map((resource) => (
                    <ResourceItem
                      key={resource.id}
                      multiselect={multiselect}
                      onChangeSelect={handleChangeSelect}
                      resource={resource}
                      selectedResources={updatedSelectedResources}
                    />
                  ))
                ) : (
                  <span className={styles.empty}>{intl.formatMessage(messages.empty)}</span>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

const mapDispatchToProps = {
  onFetchPinnedResources: fetchPinnedResources.request,
  onFetchResources: fetchResources.request,
};

export default connect(null, mapDispatchToProps)(ResourcesSelectModal);
